import { reactive } from '@rehaje/reactive'
import {
  entries,
  getIterableKeys,
  isArray,
  isTerminateValue,
  keys,
  values
} from '@rehaje/shared'

import {
  RequiredNullSchema,
  RequiredUndefinedSchema,
  RequiredUnknownSchema,
  UndefinedSchema,
  UnknownSchema
} from '../constants'
import { getPrefixName } from '../lambdas/getPrefixName'
import { SchemaDefinition } from '../models/SchemaDefinition'
import { TypedDefinition } from '../models/TypedDefinition'

import type { TypedNode } from '../models/TypedNode'
import type {
  DefaultsMergeWithSchemaContext,
  InferOnlySchemaContext,
  InferTypeScriptContext,
  InferWithSchemaContext,
  InterfaceGenerateOptions,
  Next,
  Result,
  TransformWithSchemaContext,
  TypedSchema,
  TypedSpecific,
  ValidateWithSchemaContext
} from '../types'
import type { Store } from './Store'
import type { Callback, Filter } from '@rehaje/shared'

/**
 * 类型引擎
 */
export class DefinitionStore {
  static __r_injection_node?: TypedNode

  static __r_injection_parent?: any

  static __r_injection_path?: PropertyKey[]

  static __r_injection_recursion?: boolean

  __r_definitions: Record<string, TypedDefinition> = {}

  /**
   * 类型定义表
   */
  @reactive('computed')
  get __r_definition_table(): Record<string, TypedDefinition> {
    const creating: string[] = []
    const definitionTable = this.__r_definitions
    const constructorTable = this.store.plugin.definitions
    const createTypedDefinition = (type: string) => {
      if (creating.includes(type))
        throw new Error(`Circular definition: ${type}`)
      if (definitionTable[type]) return definitionTable[type]
      if (!constructorTable[type]) return
      try {
        creating.push(type)
        const definition = TypedDefinition.__r_createTypedDefinition(
          type,
          constructorTable[type],
          this.store
        )
        if (definition.extends.length > 0) {
          const properties = definition.properties.slice()
          for (let i = definition.extends.length - 1; i >= 0; i--) {
            const extendsDefinition = createTypedDefinition(
              definition.extends[i]
            )
            if (extendsDefinition?.properties.length) {
              properties.unshift(...extendsDefinition.properties)
            }
          }
          const merged: string[] = []
          const mergedProperties: TypedSchema[] = []
          for (let i = properties.length - 1; i >= 0; i--) {
            const property = properties[i]
            const name = property.name
            if (merged.includes(name)) continue
            else if (name) merged.push(name)
            mergedProperties.push(property)
          }
          definition.properties = mergedProperties.toReversed()
        }
        definitionTable[type] = definition
        return definition
      } finally {
        creating.pop()
      }
    }
    for (const type of keys(constructorTable)) {
      createTypedDefinition(type)
    }
    return definitionTable
  }

  constructor(public store: Store) {}

  __r_applyDefaultsMergeWithSchema(
    ctx: DefaultsMergeWithSchemaContext,
    next: Next<DefaultsMergeWithSchemaContext>
  ): any {
    return this.store.plugin.applyMiddleware(
      ctx.definition,
      'defaultsMergeWithSchema',
      ctx,
      next
    )
  }

  __r_applyInferOnlySchema(
    ctx: InferOnlySchemaContext,
    next: Next<InferOnlySchemaContext>
  ): TypedSchema {
    return this.store.plugin.applyMiddleware(
      ctx.definition,
      'inferOnlySchema',
      ctx,
      next
    )
  }

  __r_applyInferTypeScript(
    ctx: InferTypeScriptContext,
    next: Next<InferTypeScriptContext>
  ) {
    return this.store.plugin.applyMiddleware(
      ctx.definition,
      'inferTypeScript',
      ctx,
      next
    )
  }

  __r_applyInferWithSchema(
    ctx: InferWithSchemaContext,
    next: Next<InferWithSchemaContext>
  ): TypedSchema {
    return this.store.plugin.applyMiddleware(
      ctx.definition,
      'inferWithSchema',
      ctx,
      next
    )
  }

  __r_applyTransformWithSchema(
    ctx: TransformWithSchemaContext,
    next: Next<TransformWithSchemaContext>
  ): any {
    return this.store.plugin.applyMiddleware(
      ctx.definition,
      'transformWithSchema',
      ctx,
      next
    )
  }

  __r_applyValidateWithSchema(
    ctx: ValidateWithSchemaContext,
    next: Next<ValidateWithSchemaContext>
  ): Error[] {
    return this.store.plugin.applyMiddleware(
      ctx.definition,
      'validateWithSchema',
      ctx,
      next
    )
  }

  __r_getDefinitionSchema(specific: TypedSpecific): TypedSchema | undefined {
    const type = `Schema.${specific.type.replace(/([^.]+).*/, '$1')}`
    const definition = this.find(type)
    if (definition) {
      return {
        type,
        value: specific
      } as TypedSchema
    }
  }

  __r_getType(payload?: any): string {
    return typeof payload?.getType === 'function'
      ? payload.getType()
      : payload?.type
  }

  /**
   * 基于 ADT 协议 推导 Shape Schema
   * @param payload
   */
  __r_inferWithDefinition(payload?: TypedSpecific): TypedSchema {
    const type = this.__r_getType(payload)
    if (type) {
      const definition = this.find(type)
      if (definition) {
        if (definition instanceof SchemaDefinition)
          return payload as TypedSchema
        return this.inferWithSchema(payload, {
          properties: definition.properties as TypedSchema[],
          type: 'Schema.Object',
          value: { type }
        })
      }
    }
    return UnknownSchema
  }

  __r_injectNode(callback: Callback, node?: TypedNode) {
    const prevNode = DefinitionStore.__r_injection_node
    const isSameNode = node === undefined || node === prevNode
    if (isSameNode) return callback()
    try {
      DefinitionStore.__r_injection_node = node ?? prevNode
      return callback()
    } finally {
      DefinitionStore.__r_injection_node = prevNode
    }
  }

  __r_injectPathNode(callback: Callback, path?: PropertyKey[], node?: TypedNode) {
    const prevPath = DefinitionStore.__r_injection_path
    const prevNode = DefinitionStore.__r_injection_node
    const isSamePath = path === undefined || path === prevPath
    const isSameNode = node === undefined || node === prevNode
    if (isSamePath && isSameNode) return callback()
    try {
      DefinitionStore.__r_injection_path = path ?? prevPath ?? []
      DefinitionStore.__r_injection_node = node ?? prevNode
      return callback()
    } finally {
      DefinitionStore.__r_injection_path = prevPath
      DefinitionStore.__r_injection_node = prevNode
    }
  }

  __r_injectPathParent(callback: Callback, path?: PropertyKey[], parent?: any) {
    const prevPath = DefinitionStore.__r_injection_path
    const prevParent = DefinitionStore.__r_injection_parent
    const isSamePath = path === undefined || path === prevPath
    const isSameParent = parent === undefined || parent === prevParent
    if (isSamePath && isSameParent) return callback()
    try {
      DefinitionStore.__r_injection_path = path ?? prevPath ?? []
      DefinitionStore.__r_injection_parent = parent ?? prevParent
      return callback()
    } finally {
      DefinitionStore.__r_injection_path = prevPath
      DefinitionStore.__r_injection_parent = prevParent
    }
  }

  __r_injectPathParentRecursion(
    callback: Callback,
    path?: PropertyKey[],
    parent?: any,
    recursion?: boolean
  ) {
    const prevPath = DefinitionStore.__r_injection_path
    const prevParent = DefinitionStore.__r_injection_parent
    const prevRecursion = DefinitionStore.__r_injection_recursion
    const isSamePath = path === undefined || path === prevPath
    const isSameParent = parent === undefined || parent === prevParent
    const isSameRecursion =
      recursion === undefined || recursion === prevRecursion
    const isSamePathParent = isSamePath && isSameParent
    if (isSamePathParent && isSameRecursion) return callback()
    if (isSamePathParent && !isSameRecursion) {
      try {
        DefinitionStore.__r_injection_recursion =
          recursion ?? prevRecursion ?? true
        return callback()
      } finally {
        DefinitionStore.__r_injection_recursion = prevRecursion
      }
    }
    if (!isSamePathParent && isSameRecursion) {
      try {
        DefinitionStore.__r_injection_path = path ?? prevPath ?? []
        DefinitionStore.__r_injection_parent = parent ?? prevParent
        return callback()
      } finally {
        DefinitionStore.__r_injection_path = prevPath
        DefinitionStore.__r_injection_parent = prevParent
      }
    }
    if (!isSamePathParent && !isSameRecursion) {
      try {
        DefinitionStore.__r_injection_path = path ?? prevPath ?? []
        DefinitionStore.__r_injection_parent = parent ?? prevParent
        DefinitionStore.__r_injection_recursion =
          recursion ?? prevRecursion ?? true
        return callback()
      } finally {
        DefinitionStore.__r_injection_path = prevPath
        DefinitionStore.__r_injection_parent = prevParent
        DefinitionStore.__r_injection_recursion = prevRecursion
      }
    }
  }

  /**
   * 基于 ADT Schema 进行默认值合并
   * @param target 参考数据
   * @param schema 参考 Schema
   * @param initialize 是否是初始化阶段
   */
  defaultsMergeWithSchema(
    target: TypedSpecific | undefined,
    schema: TypedSchema,
    initialize: boolean
  ) {
    const definition = this.find(schema.type) as SchemaDefinition
    if (definition instanceof SchemaDefinition)
      return this.__r_applyDefaultsMergeWithSchema(
        {
          definition,
          initialize,
          payload: target,
          schema,
          store: this.store
        },
        (ctx) =>
          definition.defaultsMerge(ctx.payload, ctx.schema, ctx.initialize)
      )
    return target
  }

  /**
   * 基于 ADT 类型字符串查找对应的 TypedDefinition 实例
   * @param type
   */
  find(type?: string): TypedDefinition | undefined {
    if (type) return this.__r_definition_table[type]
  }

  /**
   * 借助 filter 函数查找符合要求的 TypedDefinition 集合
   * @param filter
   */
  findAll<T extends Filter<TypedDefinition>>(filter?: T): Result<T>[]
  findAll(arg1?: any): any {
    if (arg1 === undefined) return values(this.__r_definition_table)
    else if (typeof arg1 === 'function') {
      const definitions = []
      for (const definition of values(this.__r_definition_table))
        if (arg1(definition)) definitions.push(definition)
      return definitions
    }
  }

  /**
   * 基于路径查找 Schema
   * @param schema
   * @param path
   */
  getInWithSchema(schema?: TypedSchema, path: PropertyKey[] = []): TypedSchema {
    if (!schema) return UnknownSchema
    const definition = this.find(schema.type)
    if (definition instanceof SchemaDefinition) {
      return definition.getIn(schema, path)
    }
    return UnknownSchema
  }

  /**
   * 获取所有类型定义的 TypeScript 类型
   * @returns
   */
  getTypeScript(options: InterfaceGenerateOptions) {
    const groups: Record<string, TypedDefinition[]> = { '': [] }
    for (const [key, definition] of entries(this.__r_definition_table)) {
      const [, group = ''] = getPrefixName(key)
      groups[group] ??= []
      groups[group].push(definition)
    }
    return entries(groups)
      .map(([group, definitions]) => {
        const typeScripts = definitions.map((def) => def.getTypeScript(options))
        if (!group) return typeScripts.join('\n')
        return `namespace ${group} {\n${typeScripts.join('\n')}\n}`
      })
      .join('\n')
  }

  /**
   * 判断指定类型是否存在 SchemaDefinition
   * @param type
   */
  hasSchemaDefinition(type: string | undefined) {
    if (!type) return false
    return this.hasTypedDefinition(type) && type.startsWith('Schema.')
  }

  /**
   * 判断指定类型是否存在 TypedDefinition
   * @param type
   */
  hasTypedDefinition(type: string | undefined) {
    if (!type) return false
    // eslint-disable-next-line unicorn/prefer-array-some
    return !!this.find(type)
  }

  /**
   * 基于任意数据推导对应的 ADT Schema
   * @param payload 推导参考
   */
  infer(payload?: any): TypedSchema {
    const type = this.__r_getType(payload)
    if (!this.hasTypedDefinition(type)) return this.inferShapeSchema(payload)
    return this.__r_inferWithDefinition(payload)
  }

  /**
   * 纯 Schema 推导
   * @param target
   * @returns
   */
  inferOnlySchema(target: TypedSchema | undefined): TypedSchema {
    if (target === undefined) return UndefinedSchema
    const definition = this.find(target.type) as SchemaDefinition
    if (definition instanceof SchemaDefinition)
      return this.__r_applyInferOnlySchema(
        {
          definition,
          path: DefinitionStore.__r_injection_path ?? [],
          payload: target,
          schema: target,
          store: this.store
        },
        (ctx) => definition.inferOnlySchema(ctx.payload, ctx.path)
      )
    return UnknownSchema
  }

  /**
   * 基于指定数据与指定路径推导类型，支持将纯 json 数据自动推导成具体 ADT Schema
   * @param follower
   * @param path
   * @returns
   */
  inferSchema(follower: any, path: PropertyKey[] = []): TypedSchema {
    return this.getInWithSchema(this.infer(follower), path)
  }

  /**
   * 基于真实数据推导 Shape Schema
   * @param payload
   */
  inferShapeSchema(payload?: any, visited = new WeakSet()): TypedSchema {
    if (payload === undefined) {
      return RequiredUndefinedSchema
    }
    if (payload === null) {
      return RequiredNullSchema
    }
    if (typeof payload === 'string') {
      return {
        required: true,
        type: 'Schema.String',
        value: payload
      }
    }
    if (typeof payload === 'number') {
      return {
        required: true,
        type: 'Schema.Number',
        value: payload
      }
    }
    if (typeof payload === 'boolean') {
      return {
        required: true,
        type: 'Schema.Boolean',
        value: payload
      }
    }
    if (typeof payload === 'function') {
      const type = payload.constructor.name
      const async = type === 'AsyncFunction'
      const generator = type === 'GeneratorFunction'
      return {
        async,
        generator,
        required: true,
        type: 'Schema.Function',
        value: payload
      }
    }
    if (isArray(payload)) {
      visited.add(payload)
      const items = payload.map((item) => {
        return visited.has(item)
          ? RequiredUnknownSchema
          : this.inferShapeSchema(item, visited)
      })
      return {
        items: items.every((item) => item.type === items[0].type)
          ? items[0]
          : items,
        required: true,
        type: 'Schema.Array',
        value: payload
      }
    }

    if (payload instanceof Map) {
      visited.add(payload)
      return {
        entries: Array.from(payload.entries()),
        required: true,
        type: `Schema.Map`,
        value: payload
      }
    }
    if (payload instanceof Set) {
      visited.add(payload)
      return {
        entries: Array.from(payload.entries()),
        required: true,
        type: `Schema.Set`,
        value: payload
      }
    }
    if (payload instanceof URLSearchParams) {
      visited.add(payload)
      return {
        entries: Array.from(payload.entries()),
        required: true,
        type: `Schema.URLSearchParams`,
        value: payload
      }
    }
    if (typeof payload === 'object' && !isTerminateValue(payload)) {
      visited.add(payload)
      if (typeof payload.type === 'string') {
        if (payload.type.startsWith('Schema.')) return payload
        const schema = this.__r_getDefinitionSchema(payload)
        if (schema) {
          return {
            ...schema,
            required: true
          }
        }
      }
      return {
        properties: getIterableKeys(payload).map((name) => {
          if (!name || visited.has(payload[name])) return RequiredUnknownSchema
          return {
            ...this.inferShapeSchema(payload[name], visited),
            name,
            required: true
          }
        }),
        required: true,
        type: 'Schema.Object',
        value: payload
      }
    }
    return RequiredUnknownSchema
  }

  /**
   * 基于 ADT Schema 与真实数据协同推导 TypeScript
   * @param schema 参考 Schema
   */
  inferTypeScript(schema: TypedSchema): string {
    const definition = this.find(schema.type) as SchemaDefinition
    if (definition instanceof SchemaDefinition)
      return this.__r_applyInferTypeScript(
        {
          definition,
          path: DefinitionStore.__r_injection_path ?? [],
          payload: UnknownSchema,
          schema,
          store: this.store
        },
        (ctx) => definition.inferTypeScript(ctx.schema, ctx.path)
      )
    return 'unknown'
  }

  /**
   * 基于 ADT Schema 与真实数据协同推导 Shape Schema
   * @param target 参考数据
   * @param schema 参考 Schema
   */
  inferWithSchema(
    target: TypedSpecific | undefined,
    schema: TypedSchema | undefined
  ): TypedSchema {
    if (schema === undefined) return this.inferShapeSchema(target)
    const definition = this.find(schema.type) as SchemaDefinition
    if (definition instanceof SchemaDefinition)
      return this.__r_applyInferWithSchema(
        {
          definition,
          path: DefinitionStore.__r_injection_path ?? [],
          payload: UnknownSchema,
          schema,
          store: this.store,
          target
        },
        (ctx) => definition.infer(ctx.target, ctx.schema, ctx.path)
      )
    return UnknownSchema
  }

  /**
   * 修剪数据
   * @param target
   * @param schema
   * @param path
   * @param parent
   */
  pruneWithSchema(
    target: any,
    schema: TypedSchema,
    path?: PropertyKey[],
    parent?: any
  ): any {
    const definition = this.find(schema.type) as SchemaDefinition
    if (definition instanceof SchemaDefinition)
      return definition.prune(target, schema, path, parent)
    return target
  }

  /**
   * 抛错方法
   */
  raise(error: Error) {
    DefinitionStore.__r_injection_node?.raise(error)
    this.store.raise(error)
  }

  /**
   * 判断给定数据是否符合给定 Schema 数据类型要求，当 target 为 undefined 的时候默认校验通过
   * @param target 给定数据
   * @param schema 参考 Schema
   */
  @reactive('untrack')
  testWithSchema(
    target: any,
    schema: TypedSchema,
    recursion?: boolean
  ): boolean {
    return (
      this.validateWithSchema(target, schema, recursion, false).length === 0
    )
  }

  /**
   * 基于给定 ADT Schema 转换指定数据，当 target 为 undefined 的时候默认不做任何转换
   * @param target
   * @param schema
   * @param strict
   */
  @reactive('untrack')
  transformWithSchema(target: any, schema: TypedSchema, strict = false): any {
    if ((target !== undefined || strict) && schema.type) {
      const definition = this.find(schema.type)
      if (definition instanceof SchemaDefinition) {
        return this.__r_applyTransformWithSchema(
          {
            definition,
            path: DefinitionStore.__r_injection_path ?? [],
            payload: target,
            schema,
            store: this.store
          },
          (ctx) => definition.transform(ctx.payload, schema, ctx.path)
        )
      }
    }
    if (target === undefined) return schema.fallback
    return target
  }

  @reactive('untrack')
  validateWithSchema(
    target: any,
    schema: TypedSchema,
    recursion?: boolean,
    dispatch?: boolean,
    strict?: boolean
  ): Error[] {
    if (!strict && target === undefined) return []
    const definition = this.find(schema?.type)
    if (!(definition instanceof SchemaDefinition)) return []
    return this.__r_applyValidateWithSchema(
      {
        definition,
        parent: DefinitionStore.__r_injection_parent,
        path: DefinitionStore.__r_injection_path ?? [],
        payload: [],
        recursion: recursion ?? DefinitionStore.__r_injection_recursion ?? true,
        schema,
        store: this.store,
        strict,
        target
      },
      (ctx) => {
        return this.store.catch(
          () => {
            definition.validate(
              ctx.target,
              schema,
              ctx.path,
              ctx.parent,
              ctx.recursion
            )
          },
          { dispatch }
        )
      }
    )
  }
}
